// Interface admin — accessible uniquement aux users ayant le rôle 'admin'
// (gn_user_admin_role). Chaque KPI est présenté sous forme de card avec la
// valeur courante et une sparkline sur les N derniers jours.
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { isLoggedIn, verifySession, isAdmin, getAdminOverview, getUser } from "../services/authService";
import Sparkline from "../components/Sparkline";
import AdminUsersPanel from "../components/AdminUsersPanel";
import AdminAuditLogPanel from "../components/AdminAuditLogPanel";
import AdminSyncErrorsPanel from "../components/AdminSyncErrorsPanel";
import AdminSyncFailuresPanel from "../components/AdminSyncFailuresPanel";
import AdminBroadcastPanel from "../components/AdminBroadcastPanel";
import AdminModerationPanel from "../components/AdminModerationPanel";

function formatNumber(v) {
	if (v == null) return "…";
	if (typeof v !== "number") return String(v);
	if (Number.isInteger(v)) return v.toLocaleString("fr-FR");
	return v.toLocaleString("fr-FR", { maximumFractionDigits: 1 });
}

// Calcule la variation en % entre le dernier et l'avant-dernier point non-null.
function computeTrend(series) {
	const cleaned = (series || []).filter((d) => d.value != null);
	if (cleaned.length < 2) return null;
	const last = Number(cleaned[cleaned.length - 1].value);
	const prev = Number(cleaned[cleaned.length - 2].value);
	if (prev === 0) return last === 0 ? 0 : null;
	return ((last - prev) / Math.abs(prev)) * 100;
}

function KpiCard({ label, kpi, unit = "", color = "#30a49a", hint = null, invert = false }) {
	const current = kpi?.current;
	const series = kpi?.series || [];
	const trend = computeTrend(series);
	const trendColor = trend == null ? "var(--ink-soft)"
		: trend > 0
			? (invert ? "#e57373" : "#4ade80")
			: trend < 0
				? (invert ? "#4ade80" : "#e57373")
				: "var(--ink-soft)";
	const trendSign = trend == null ? "" : trend > 0 ? "▲" : trend < 0 ? "▼" : "—";

	return (
		<div className="admin-kpi-card" style={{ borderLeftColor: color }}>
			<div className="admin-kpi-header">
				<span className="admin-kpi-label">{label}</span>
				{trend != null && (
					<span className="admin-kpi-trend" style={{ color: trendColor }}>
						{trendSign} {Math.abs(trend).toFixed(1)}%
					</span>
				)}
			</div>
			<div className="admin-kpi-value">
				{formatNumber(current)}
				{unit && <span className="admin-kpi-unit">{unit}</span>}
			</div>
			<div className="admin-kpi-spark">
				<Sparkline data={series} color={color} />
			</div>
			{hint && <div className="admin-kpi-hint">{hint}</div>}
		</div>
	);
}

export default function AdminDashboard() {
	const navigate = useNavigate();
	const [ready, setReady] = useState(false);
	const [overview, setOverview] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState("");
	const [days, setDays] = useState(30);
	const [activeTab, setActiveTab] = useState("kpi");

	useEffect(() => {
		(async () => {
			if (!isLoggedIn()) {
				navigate("/login");
				return;
			}
			const ok = await verifySession();
			if (!ok) {
				navigate("/login");
				return;
			}
			if (!isAdmin()) {
				navigate("/dashboard");
				return;
			}
			setReady(true);
		})();
	}, [navigate]);

	const loadOverview = async (d) => {
		setLoading(true);
		setError("");
		const result = await getAdminOverview(d);
		setLoading(false);
		if (result.success) setOverview(result.overview);
		else setError(result.error || "Erreur");
	};

	useEffect(() => {
		if (ready) loadOverview(days);
	}, [ready, days]);

	if (!ready) return null;

	const user = getUser();
	const kpis = overview?.kpis || {};

	return (
		<div className="page">
			<div className="admin-container">
				<header className="admin-header">
					<div>
						<h1 className="admin-title">Interface admin</h1>
						<p className="admin-subtitle">
							Connecté en tant que <strong>{user?.prenom} {user?.nom}</strong> · rôles :{" "}
							{(user?.adminRoles || []).map((r) => <span key={r} className="admin-role-chip">{r}</span>)}
						</p>
					</div>
					{activeTab === "kpi" && (
						<div className="admin-period-picker">
							<label>Fenêtre :</label>
							<select value={days} onChange={(e) => setDays(parseInt(e.target.value, 10))}>
								<option value={7}>7 derniers jours</option>
								<option value={14}>14 derniers jours</option>
								<option value={30}>30 derniers jours</option>
								<option value={60}>60 derniers jours</option>
								<option value={90}>90 derniers jours</option>
							</select>
							<button className="mens-btn ghost small" onClick={() => loadOverview(days)}>
								Rafraîchir
							</button>
						</div>
					)}
				</header>

				<div className="admin-tabs">
					<button
						className={`admin-tab ${activeTab === "kpi" ? "active" : ""}`}
						onClick={() => setActiveTab("kpi")}
					>
						Indicateurs
					</button>
					<button
						className={`admin-tab ${activeTab === "users" ? "active" : ""}`}
						onClick={() => setActiveTab("users")}
					>
						Utilisateurs
					</button>
					<button
						className={`admin-tab ${activeTab === "moderation" ? "active" : ""}`}
						onClick={() => setActiveTab("moderation")}
					>
						Modération
					</button>
					<button
						className={`admin-tab ${activeTab === "broadcast" ? "active" : ""}`}
						onClick={() => setActiveTab("broadcast")}
					>
						Broadcast
					</button>
					<button
						className={`admin-tab ${activeTab === "audit" ? "active" : ""}`}
						onClick={() => setActiveTab("audit")}
					>
						Journal d'audit
					</button>
					<button
						className={`admin-tab ${activeTab === "sync-errors" ? "active" : ""}`}
						onClick={() => setActiveTab("sync-errors")}
					>
						Erreurs sync
					</button>
					<button
						className={`admin-tab ${activeTab === "sync-failures" ? "active" : ""}`}
						onClick={() => setActiveTab("sync-failures")}
					>
						Payloads rejetés
					</button>
				</div>

				{activeTab === "users" && <AdminUsersPanel />}
				{activeTab === "moderation" && <AdminModerationPanel />}
				{activeTab === "broadcast" && <AdminBroadcastPanel />}
				{activeTab === "audit" && <AdminAuditLogPanel />}
				{activeTab === "sync-errors" && <AdminSyncErrorsPanel />}
				{activeTab === "sync-failures" && <AdminSyncFailuresPanel />}

				{activeTab === "kpi" && (
					<>
				{error && <div className="admin-error">{error}</div>}
				{loading && <div className="admin-loading">Chargement des KPI...</div>}

				<section className="admin-section">
					<h2 className="admin-section-title">Utilisateurs</h2>
					<div className="admin-kpi-grid">
						<KpiCard label="Total inscrits" kpi={kpis.totalUsers} color="#30a49a" hint="Cumulé jour par jour" />
						<KpiCard label="Nouveaux inscrits (jour)" kpi={kpis.newUsers} color="#30a49a" />
						<KpiCard label="Rétention D+7" kpi={kpis.retentionD7} unit=" %" color="#a78bfa" hint="% de la cohorte inscrite J-7 encore active dans les 7 jours" />
					</div>
				</section>

				<section className="admin-section">
					<h2 className="admin-section-title">Engagement</h2>
					<div className="admin-kpi-grid">
						<KpiCard label="DAU (users actifs)" kpi={kpis.dau} color="#30a49a" hint="Créé/modifié ≥ 1 séance ce jour" />
						<KpiCard label="WAU (fenêtre 7 j)" kpi={kpis.wau} color="#30a49a" hint="Users uniques sur les 7 jours précédents" />
						<KpiCard label="Stickiness DAU/WAU" kpi={kpis.stickiness} unit=" %" color="#a78bfa" hint="Ratio de fidélité — plus haut = plus régulier" />
					</div>
				</section>

				<section className="admin-section">
					<h2 className="admin-section-title">Activité</h2>
					<div className="admin-kpi-grid">
						<KpiCard label="Séances créées (jour)" kpi={kpis.sessions} color="#30a49a" />
					</div>
				</section>

				<section className="admin-section">
					<h2 className="admin-section-title">Monétisation</h2>
					<div className="admin-kpi-grid">
						<KpiCard label="GymNote Pro actifs" kpi={kpis.proActive} color="#F87B1B" />
						<KpiCard label="GymNote+ actifs" kpi={kpis.starterActive} color="#F87B1B" />
						<KpiCard label="Comptes gratuits" kpi={kpis.freeUsers} color="#30a49a" />
						<KpiCard label="MRR" kpi={kpis.mrr} unit=" €" color="#F87B1B" hint="Revenu mensuel récurrent" />
					</div>
				</section>

				<section className="admin-section">
					<h2 className="admin-section-title">Social</h2>
					<div className="admin-kpi-grid">
						<KpiCard label="Réactions (jour)" kpi={kpis.reactions} color="#a78bfa" />
						<KpiCard label="Commentaires (jour)" kpi={kpis.comments} color="#a78bfa" />
					</div>
				</section>

				<footer className="admin-footer">
					{overview?.period && (
						<span>
							Période : {overview.period.start} → {overview.period.end} · Générées le{" "}
							{overview.generatedAt ? new Date(overview.generatedAt).toLocaleString("fr-FR") : "—"}
						</span>
					)}
				</footer>
					</>
				)}
			</div>
		</div>
	);
}
